import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  createElement,
  useMemo,
  useState,
} from 'react'
import {
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { WorkItemTypeDialog } from '../components/WorkItemTypeDialog'
import { DeleteWorkItemTypeDialog } from '../components/DeleteWorkItemTypeDialog'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import type { WorkItemTypeDocument } from '@/lib/db/rxdb/schemas/workItemType'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { usePageLayout } from '@/hooks/usePageLayout'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPageContainer, SettingsPageHeader } from '@/components/app/layout'
import { useWorkItemTypes } from '@/lib/db/rxdb/hooks/entities/work-item-types'
import { WorkItemTypeIcon } from '@/components/app/tasks/WorkItemTypeIcon'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/work-item-types',
)({
  beforeLoad: ({ params }) => {
    if (!FEATURE_FLAGS.ENABLE_WORK_ITEM_TYPES) {
      throw redirect({ to: '/$workspaceSlug/settings', params })
    }
  },
  component: WorkItemTypeSettingsPage,
})

export default function WorkItemTypeSettingsPage() {
  const { t } = useTranslation('settings')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const { workItemTypes, isLoading } = useWorkItemTypes({ isDeleted: false })

  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [editItem, setEditItem] = useState<WorkItemTypeDocument | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteItem, setDeleteItem] = useState<{ id: string; name: string } | null>(null)

  const normalizeText = (value?: string | null) =>
    value?.toLowerCase() ?? ''

  const searchTerm = normalizeText(search.trim())
  const isSearching = Boolean(searchTerm)

  const filteredTypes = useMemo(() => {
    if (!isSearching) return workItemTypes
    return workItemTypes.filter((type) => {
      const matchesName = normalizeText(type.name).includes(searchTerm)
      const matchesDescription = normalizeText(type.description).includes(searchTerm)
      return matchesName || matchesDescription
    })
  }, [isSearching, searchTerm, workItemTypes])

  const handleCreate = () => {
    setDialogMode('create')
    setEditItem(null)
    setDialogOpen(true)
  }

  const handleEdit = (type: WorkItemTypeDocument) => {
    setDialogMode('edit')
    setEditItem(type)
    setDialogOpen(true)
  }

  const handleDelete = (type: WorkItemTypeDocument) => {
    setDeleteItem({ id: type.id, name: type.name })
    setDeleteDialogOpen(true)
  }

  const columns = useMemo<Array<ColumnDef<WorkItemTypeDocument>>>(() => {
    return [
      {
        accessorKey: 'name',
        header: () => <span>{t('workItemTypes.columns.name')}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const type = row.original
          return (
            <div className="flex items-center gap-2">
              <WorkItemTypeIcon icon={type.icon} color={type.color} />
              <span className="font-medium text-sm text-foreground">
                {type.name}
              </span>
              {type.isSystem && (
                <Lock className="size-3 text-muted-foreground" />
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'description',
        header: () => <span>{t('workItemTypes.columns.description')}</span>,
        cell: ({ row }) => {
          const description = row.original.description
          return description ? (
            <span className="text-sm text-muted-foreground">{description}</span>
          ) : (
            <span className="text-sm text-muted-foreground/60">
              {t('workItemTypes.noDescription')}
            </span>
          )
        },
      },
      {
        id: 'createdOn',
        accessorFn: (row) => row.createdOn ?? 0,
        header: () => <span>{t('workItemTypes.columns.created')}</span>,
        cell: ({ row }) => (
          <WorkItemTypeCreatedCell ts={row.original.createdOn} />
        ),
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => {
          const type = row.original
          if (type.isSystem) return null
          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="size-8">
                    <MoreHorizontal className="size-4" />
                    <span className="sr-only">{t('workItemTypes.actionsAria')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleEdit(type)}>
                    <Pencil className="size-4 mr-2" />
                    {t('workItemTypes.actions.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDelete(type)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="size-4 mr-2" />
                    {t('workItemTypes.actions.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ]
  }, [t])

  return (
    <SettingsPageContainer innerClassName="space-y-6">
        <SettingsPageHeader
          title={t('workItemTypes.title')}
          action={
            <Button size="sm" onClick={handleCreate}>
              <Plus className="size-4 mr-1" />
              {t('workItemTypes.newButton')}
            </Button>
          }
        />

        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full max-w-xs">
            <Input
              placeholder={t('workItemTypes.filterPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-9"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex h-64 items-center justify-center rounded-md border bg-card">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <WorkItemTypeDataTable columns={columns} data={filteredTypes} />
        )}

        <WorkItemTypeDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          mode={dialogMode}
          initialItem={editItem}
          existingTypes={workItemTypes.map((t) => ({ id: t.id, name: t.name }))}
        />

        <DeleteWorkItemTypeDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          typeId={deleteItem?.id ?? null}
          typeName={deleteItem?.name ?? ''}
        />
    </SettingsPageContainer>
  )
}

function WorkItemTypeCreatedCell({ ts }: { ts: number | null | undefined }) {
  const { smartTime, dateTime: formatDateTime } = useDateTimeFormatters()
  if (!ts) {
    return <span className="text-sm text-muted-foreground">\u2014</span>
  }
  const date = new Date(ts)
  return (
    <time
      className="text-sm text-muted-foreground"
      title={formatDateTime(date, { dateStyle: 'medium', timeStyle: 'short' })}
      dateTime={date.toISOString()}
    >
      {smartTime(date)}
    </time>
  )
}

function WorkItemTypeDataTable({
  columns,
  data,
}: {
  columns: Array<ColumnDef<WorkItemTypeDocument>>
  data: Array<WorkItemTypeDocument>
}) {
  const { t } = useTranslation('settings')
  const [sorting, setSorting] = useState<SortingState>([])

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="rounded-md border bg-card">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {t('workItemTypes.noTypesFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
